import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Building2, Send } from "lucide-react";
import { fmtMoney, PAYMENT_STATUS_TONE } from "./billingMeta";
import { endpoints } from "@/lib/api";

export default function ClientHistoryDrawer({ open, onOpenChange, client, onRemind }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (open && client) {
      endpoints.clientBillingHistory(client).then(setData).catch(() => setData(null));
    }
  }, [open, client]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="client-history-drawer" side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl brand-gradient flex items-center justify-center shadow-md shadow-indigo-500/25">
              <Building2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <SheetTitle>{client}</SheetTitle>
              <SheetDescription>Full billing history</SheetDescription>
            </div>
          </div>
        </SheetHeader>

        {data && (
          <>
            <div className="grid grid-cols-3 gap-2 my-4">
              <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 bg-white/60 dark:bg-slate-900/40">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Billed</div>
                <div className="text-lg font-bold font-mono">{fmtMoney(data.total_billed)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 bg-white/60 dark:bg-slate-900/40">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Paid</div>
                <div className="text-lg font-bold font-mono text-emerald-600">{fmtMoney(data.total_paid)}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-white/10 p-3 bg-white/60 dark:bg-slate-900/40">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Outstanding</div>
                <div className="text-lg font-bold font-mono text-rose-600">{fmtMoney(data.total_outstanding)}</div>
              </div>
            </div>

            <div className="space-y-2 pb-4">
              {data.invoices.map((inv) => (
                <div key={inv.id} data-testid={`history-invoice-${inv.id}`} className="rounded-2xl border border-slate-200 dark:border-white/10 bg-white/60 dark:bg-slate-900/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-mono text-xs font-semibold">{inv.invoice_number}</div>
                    <Badge variant="outline" className={`text-[10px] rounded-full ${PAYMENT_STATUS_TONE[inv.status] || ""}`}>{inv.status}</Badge>
                  </div>
                  <div className="text-sm mt-1 truncate">{inv.description}</div>
                  <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-x-3">
                    <span>Invoiced {inv.invoice_date}</span>
                    <span>Due {inv.due_date}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="text-sm font-mono">
                      <span className="text-slate-500">{fmtMoney(inv.amount_paid)}</span>
                      <span className="text-slate-400 mx-1">of</span>
                      <span className="font-bold">{fmtMoney(inv.amount)}</span>
                    </div>
                    {inv.outstanding > 0 && inv.status !== "Draft" && (
                      <Button
                        data-testid={`history-remind-${inv.id}`}
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs"
                        onClick={() => onRemind(inv)}
                      >
                        <Send className="h-3 w-3 mr-1" /> Remind
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {data.invoices.length === 0 && (
                <div className="text-center text-sm text-slate-500 py-8">No invoices yet.</div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
